import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CallRangeDto, DailySummaryDto } from '@geotracker/contracts';
import { api } from '@/lib/api/client';
import { Card, StatTile } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Empty, ErrorState, Loading } from '@/components/ui/state';
import { DASH, formatClock, formatDistance, formatDuration } from '@/lib/format';
import { cn } from '@/lib/utils';

/** Minimal shape of the roster rows we need for the selector. */
interface EmployeeRow {
  employeeId: number;
  name: string;
  employeeCode: string;
}

type DirFilter = 'all' | 'incoming' | 'outgoing' | 'missed';

function ymd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** [from, to] for the last `days` days inclusive of today, in local time. */
function lastDays(days: number): { from: string; to: string } {
  const today = new Date();
  const start = new Date();
  start.setDate(today.getDate() - (days - 1));
  return { from: ymd(start), to: ymd(today) };
}

const PRESETS: { label: string; days: number }[] = [
  { label: 'Today', days: 1 },
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
];

/**
 * Bulk activity view: pick a user and a date range (preset or custom) and see
 * all their call activity and per-day location summaries together, with
 * client-side filters on the calls.
 */
export function ActivityPage() {
  const initial = lastDays(30);
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [dir, setDir] = useState<DirFilter>('all');
  const [search, setSearch] = useState('');

  const roster = useQuery({
    queryKey: ['employees'],
    queryFn: () => api.get<EmployeeRow[]>('/admin/employees'),
  });
  const employees = roster.data?.data ?? [];

  const enabled = employeeId !== null && !!from && !!to;

  const callsQ = useQuery({
    queryKey: ['calls-range', employeeId, from, to],
    queryFn: () =>
      api.get<CallRangeDto>(
        `/admin/employees/${employeeId}/calls/range?from=${from}&to=${to}`,
      ),
    enabled,
  });

  const summaryQ = useQuery({
    queryKey: ['loc-summary', employeeId, from, to],
    queryFn: () =>
      api.get<DailySummaryDto>(
        `/admin/employees/${employeeId}/location-summary?from=${from}&to=${to}`,
      ),
    enabled,
  });

  const calls = callsQ.data?.data;
  const summary = summaryQ.data?.data;
  const tz = summary?.timezone ?? calls?.timezone;

  const activePreset = useMemo(() => {
    for (const p of PRESETS) {
      const r = lastDays(p.days);
      if (r.from === from && r.to === to) return p.days;
    }
    return null;
  }, [from, to]);

  const filteredCalls = useMemo(() => {
    const list = calls?.calls ?? [];
    const q = search.trim().toLowerCase();
    return list.filter((c) => {
      if (dir === 'missed') {
        if (c.direction !== 'missed' && c.direction !== 'rejected') return false;
      } else if (dir !== 'all' && c.direction !== dir) {
        return false;
      }
      if (q) {
        const inNumber = c.number.toLowerCase().includes(q);
        const inName = (c.contactName ?? '').toLowerCase().includes(q);
        if (!inNumber && !inName) return false;
      }
      return true;
    });
  }, [calls, dir, search]);

  function applyPreset(days: number) {
    const r = lastDays(days);
    setFrom(r.from);
    setTo(r.to);
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-4">
      <div>
        <h1 className="text-lg font-semibold">Activity</h1>
        <p className="text-sm text-muted-foreground">
          Call activity and daily location summaries for one person over a date
          range.
        </p>
      </div>

      {/* Controls */}
      <Card className="flex flex-col gap-3 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Employee</span>
            <select
              className="min-w-56 rounded-md border border-border bg-background px-2 py-1.5"
              value={employeeId ?? ''}
              onChange={(e) =>
                setEmployeeId(e.target.value ? Number(e.target.value) : null)
              }
            >
              <option value="">Select an employee…</option>
              {employees.map((e) => (
                <option key={e.employeeId} value={e.employeeId}>
                  {e.name} ({e.employeeCode})
                </option>
              ))}
            </select>
          </label>

          <div className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Quick range</span>
            <div className="flex gap-1">
              {PRESETS.map((p) => (
                <button
                  key={p.days}
                  type="button"
                  onClick={() => applyPreset(p.days)}
                  className={cn(
                    'rounded-md border px-3 py-1.5',
                    activePreset === p.days
                      ? 'border-primary bg-primary/10 font-medium text-primary'
                      : 'border-border bg-background hover:bg-muted',
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">From</span>
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded-md border border-border bg-background px-2 py-1.5"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">To</span>
            <input
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
              className="rounded-md border border-border bg-background px-2 py-1.5"
            />
          </label>
        </div>
      </Card>

      {employeeId === null ? (
        <Empty title="Pick an employee" hint="Choose someone above to load their activity." />
      ) : (
        <>
          {/* Location daily summaries */}
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-semibold">Location — daily summary</h2>
              {summary ? (
                <span className="text-sm text-muted-foreground">
                  {summary.from} → {summary.to}
                  {tz ? ` · ${tz}` : ''}
                </span>
              ) : null}
            </div>

            {summaryQ.isPending ? (
              <Loading label="Loading location summary…" />
            ) : summaryQ.error ? (
              <ErrorState error={summaryQ.error} onRetry={() => void summaryQ.refetch()} />
            ) : !summary || summary.days.length === 0 ? (
              <Empty title="No location data" hint="No accepted fixes in this range." />
            ) : (
              <>
                <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <StatTile label="Distance" value={formatDistance(summary.totals.distanceMeters)} />
                  <StatTile label="Active days" value={String(summary.totals.activeDays)} />
                  <StatTile label="Stops" value={String(summary.totals.stopCount)} />
                  <StatTile label="Fixes" value={summary.totals.pointCount.toLocaleString()} />
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-muted-foreground">
                      <tr className="border-b border-border">
                        <th className="py-2 pr-3 font-medium">Date</th>
                        <th className="py-2 pr-3 font-medium">Distance</th>
                        <th className="py-2 pr-3 font-medium">Moving</th>
                        <th className="py-2 pr-3 font-medium">Stops</th>
                        <th className="py-2 pr-3 font-medium">Fixes</th>
                        <th className="py-2 pr-3 font-medium">First</th>
                        <th className="py-2 pr-3 font-medium">Last</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.days.map((d) => (
                        <tr key={d.date} className="border-b border-border/60">
                          <td className="py-2 pr-3 font-medium tabular-nums">{d.date}</td>
                          <td className="py-2 pr-3 tabular-nums">{formatDistance(d.distanceMeters)}</td>
                          <td className="py-2 pr-3 tabular-nums">{formatDuration(d.movingSeconds)}</td>
                          <td className="py-2 pr-3 tabular-nums">
                            {d.stopCount}
                            {d.longStopCount > 0 ? (
                              <span className="ml-1 text-muted-foreground">({d.longStopCount} long)</span>
                            ) : null}
                          </td>
                          <td className="py-2 pr-3 tabular-nums">{d.pointCount.toLocaleString()}</td>
                          <td className="py-2 pr-3 tabular-nums">{formatClock(d.firstPointAt, tz)}</td>
                          <td className="py-2 pr-3 tabular-nums">{formatClock(d.lastPointAt, tz)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>

          {/* Call logs */}
          <Card className="p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-semibold">Call logs</h2>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                  value={dir}
                  onChange={(e) => setDir(e.target.value as DirFilter)}
                >
                  <option value="all">All directions</option>
                  <option value="incoming">Incoming</option>
                  <option value="outgoing">Outgoing</option>
                  <option value="missed">Missed / rejected</option>
                </select>
                <input
                  type="search"
                  placeholder="Search number or name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                />
              </div>
            </div>

            {callsQ.isPending ? (
              <Loading label="Loading call logs…" />
            ) : callsQ.error ? (
              <ErrorState error={callsQ.error} onRetry={() => void callsQ.refetch()} />
            ) : !calls || calls.calls.length === 0 ? (
              <Empty title="No call activity" hint="No calls recorded in this range." />
            ) : (
              <>
                <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
                  <StatTile label="Total" value={String(calls.summary.total)} />
                  <StatTile label="Incoming" value={String(calls.summary.incoming)} />
                  <StatTile label="Outgoing" value={String(calls.summary.outgoing)} />
                  <StatTile label="Missed" value={String(calls.summary.missed)} />
                  <StatTile label="Talk time" value={formatDuration(calls.summary.totalDurationSeconds)} />
                </div>

                {filteredCalls.length === 0 ? (
                  <Empty title="Nothing matches that filter" />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-left text-muted-foreground">
                        <tr className="border-b border-border">
                          <th className="py-2 pr-3 font-medium">When</th>
                          <th className="py-2 pr-3 font-medium">Direction</th>
                          <th className="py-2 pr-3 font-medium">Number</th>
                          <th className="py-2 pr-3 font-medium">Contact</th>
                          <th className="py-2 pr-3 font-medium">Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCalls.map((c, i) => (
                          <tr key={`${c.startedAt}-${i}`} className="border-b border-border/60">
                            <td className="py-2 pr-3 tabular-nums">{formatClock(c.startedAt, tz)}</td>
                            <td className="py-2 pr-3">
                              <Badge
                                tone={
                                  c.direction === 'incoming'
                                    ? 'moving'
                                    : c.direction === 'outgoing'
                                      ? 'muted'
                                      : 'stopped'
                                }
                              >
                                {c.direction}
                              </Badge>
                            </td>
                            <td className="py-2 pr-3 tabular-nums">{c.number || DASH}</td>
                            <td className="py-2 pr-3">{c.contactName || DASH}</td>
                            <td className="py-2 pr-3 tabular-nums">
                              {c.durationSeconds > 0 ? formatDuration(c.durationSeconds) : DASH}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Showing {filteredCalls.length} of {calls.calls.length} calls.
                    </p>
                  </div>
                )}
              </>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
